import { Gender, SelectionTitleLists, SelectionType } from '@/data/enums'
import { imageTitles } from '@/data/selections'
import Tile from './Tile'
import TilePayload from './TilePayload'
import ClientProfile, { IClientProfile } from '@/store/models/ClientProfile'

export default class ProfileModel {
  public age: number
  public adviceLenses: boolean = false
  public adviceMultifocal: boolean = true
  public adviceReadingGlasses: boolean = false
  public adviceScreenGlasses: boolean = true
  public adviceSportGlasses: boolean = false
  public adviceSunGlasses: boolean = false
  public name: string
  public gender: Gender
  public genders: Tile[] = []
  public activities: Tile[] = []
  public sports: Tile[] = []
  public screens: Tile[] = []
  public transport: Tile[] = []
  public glasses: Tile[] = []

  private canDrive: boolean = true
  private needsReadingGlasses: boolean = true

  constructor (profile?: IClientProfile) {
    this.age = (profile) ? profile.age : 44
    this.name = (profile) ? profile.name : ''
    this.gender = (profile)
      ? (profile.gender === 0) ? Gender.Male : Gender.Female
      : Gender.Male

    const selectedGender = this.gender === Gender.Male ? 0 : 1
    const clientProfile = profile || new ClientProfile('', undefined, this)

    // set tiles
    this.createImageTiles(SelectionType.Activity, 12, false, clientProfile.selected[SelectionType.Activity])
    this.createImageTiles(SelectionType.Gender, 2, false, [selectedGender]) // genders
    this.createImageTiles(
      SelectionType.Glasses,
      6,
      true,
      clientProfile.selected[SelectionType.Glasses],
      clientProfile.values[SelectionType.Glasses]
    ) // glasses
    this.createImageTiles(
      SelectionType.Screen,
      6,
      true,
      clientProfile.selected[SelectionType.Screen],
      clientProfile.values[SelectionType.Screen]
    ) // screens
    this.createImageTiles(SelectionType.Sport, 9, false, clientProfile.selected[SelectionType.Sport]) // sports
    this.createImageTiles(
      SelectionType.Transport,
      6,
      true,
      clientProfile.selected[SelectionType.Transport],
      clientProfile.values[SelectionType.Transport]
    ) // transport

    this.update()
  }

  public changeSelection (payload: TilePayload) {
    if (payload.item) {
      const itemList = this.getItemList(payload.type)
      switch (payload.type) {
        case SelectionType.Gender:
          const genderTileIndex = itemList.findIndex((item) => item.id === payload.item.id)
          if (genderTileIndex !== -1) {
            itemList[genderTileIndex] = payload.item
            if (payload.item.selected) {
              if (payload.item.id === '0') {
                this.gender = Gender.Male
              } else {
                this.gender = Gender.Female
              }
            } else {
              if (payload.item.id === '0') {
                this.gender = Gender.Female
              } else {
                this.gender = Gender.Male
              }
            }
            const otherGender = itemList.find((item) => item.id !== payload.item.id)
            if (otherGender) {
              otherGender.selected = !payload.item.selected
            }
            this.update()
            return itemList
          }
          break
        case SelectionType.Activity:
        case SelectionType.Glasses:
        case SelectionType.Screen:
        case SelectionType.Sport:
        case SelectionType.Transport:
          const tileIndex = itemList.findIndex((item) => item.id === payload.item.id)
          if (tileIndex !== -1) {
            itemList[tileIndex] = payload.item
            this.update()
            return itemList
          }
      }
    }
    return []
  }

  public getAge () {
    return this.age
  }

  public getAdviceLenses () {
    return this.adviceLenses
  }

  public getAdviceMultifocal () {
    return this.adviceMultifocal
  }

  public getAdviceReadingGlasses () {
    return this.adviceReadingGlasses
  }

  public getAdviceScreenGlasses () {
    return this.adviceScreenGlasses
  }

  public getAdviceSportGlasses () {
    return this.adviceSportGlasses
  }

  public getAdviceSunGlasses () {
    return this.adviceSunGlasses
  }

  public getGender () {
    return this.gender
  }

  public getItemList (type: SelectionType) {
    switch (type) {
      case SelectionType.Activity:
        return this.activities
      case SelectionType.Gender:
        return this.genders
      case SelectionType.Glasses:
        return this.glasses
      case SelectionType.Screen:
        return this.screens
      case SelectionType.Sport:
        return this.sports
      case SelectionType.Transport:
        return this.transport
      default:
        return []
    }
  }

  public getItemTitles (type: SelectionType) {
    switch (type) {
      case SelectionType.Activity:
        if (this.age < 36) {
          return imageTitles[SelectionTitleLists.Activity]
        } else {
          return imageTitles[SelectionTitleLists.ActivityOverThirtySix]
        }
      case SelectionType.Gender:
        return imageTitles[SelectionTitleLists.Gender]
      case SelectionType.Glasses:
        return imageTitles[SelectionTitleLists.Glasses]
      case SelectionType.Screen:
        return imageTitles[SelectionTitleLists.Screen]
      case SelectionType.Sport:
        return imageTitles[SelectionTitleLists.Sport]
      case SelectionType.Transport:
        return imageTitles[SelectionTitleLists.Transport]
      default:
        return []
    }
  }

  public getName () {
    return this.name
  }

  public setAge (age: number) {
    // @todo Separate glass advice update (performance).
    let update = false
    if (this.age < 18 && age >= 18) {
      update = true
    } else if (this.age >= 18 && age < 18) {
      update = true
    } else if (this.age < 36 && age >= 36) {
      update = true
    } else if (this.age >= 36 && age < 36) {
      update = true
    } else if (this.age < 43 && age >= 43) {
      update = true
    } else if (this.age >= 43 && age < 43) {
      update = true
    } else if (this.age < 65 && age >= 65) {
      update = true
    } else if (this.age >= 65 && age < 65) {
      update = true
    }
    this.age = age
    if (update) {
      this.update()
    }
  }

  public setGender (gender: Gender) {
    this.gender = gender
    if (this.gender === Gender.Male) {
      this.genders[0].selected = true
      this.genders[1].selected = false
    } else {
      this.genders[0].selected = false
      this.genders[1].selected = true
    }

    this.update()
  }

  public setName (name: string) {
    this.name = name
  }

  public update () {
    let agePath = ''
    let genderPath = ''
    let selectGenderPath = ''

    // GENDER
    if (this.gender === Gender.Male) {
      genderPath = 'male_'
    } else {
      genderPath = 'female_'
    }

    // AGE SELECT
    if (this.age < 36) {
      selectGenderPath = '36-'
    } else if ((this.age >= 36) && (this.age < 65)) {
      selectGenderPath = '36'
    } else {
      selectGenderPath = '65'
    }

    // CHECK AGE
    if (this.age < 36) {
      agePath = '36-'
      this.needsReadingGlasses = false
    } else if ((this.age >= 36) && (this.age < 65)) {
      agePath = '36'
      this.needsReadingGlasses = true
    } else {
      agePath = '65'
      this.needsReadingGlasses = true
    }

    // CAN DRIVE
    this.canDrive = this.age > 17

    // SET GENDERS
    const genderImagePath = `profile/age/${selectGenderPath}/gender`
    this.updateImageTiles(SelectionType.Gender, genderImagePath, this.getItemTitles(SelectionType.Gender))

    // SET SPORTS
    const sportImagePath = `profile/sport/${agePath}/sport`
    this.updateImageTiles(SelectionType.Sport, sportImagePath, this.getItemTitles(SelectionType.Sport))

    // SET ACTIVITIES
    const ageActivityImagePath = `profile/activity/${agePath}/activity`
    this.updateImageTiles(SelectionType.Activity, ageActivityImagePath, this.getItemTitles(SelectionType.Activity))

    // SET TRANSPORT
    const transportImagePath = `profile/transport/${agePath}/${genderPath}transport`
    const disabledTransportItems = (this.canDrive)
      ? []
      : [0, 1, 2, 3]
    this.updateImageTiles(
      SelectionType.Transport,
      transportImagePath,
      this.getItemTitles(SelectionType.Transport),
      disabledTransportItems
    )

    // SET SCREENS
    const screenImagePath = `profile/screen/${agePath}/${genderPath}screen`
    this.updateImageTiles(SelectionType.Screen, screenImagePath, this.getItemTitles(SelectionType.Screen))

    // SET GLASSES
    const glassesImagePath = `profile/glasses/${agePath}/${genderPath}glass`
    const disabledGlassesItems = (this.needsReadingGlasses)
      ? []
      : [1, 2]
    this.updateImageTiles(
      SelectionType.Glasses,
      glassesImagePath,
      this.getItemTitles(SelectionType.Glasses),
      disabledGlassesItems
    )

    // Update
    this.runAlgorithm()
  }

  private runAlgorithm () {
    let pointsScreen = 0
    let pointsReading = 0
    let pointsSun = 0
    let pointsSport = 0
    // screen
    // smartphone
    if (this.screens) {
      if (this.screens[0].selected) {
        if (this.screens[0].value) {
          if (this.screens[0].value === 0) {
            pointsScreen += 2
            pointsReading += 1
          } else if (this.screens[0].value === 1) {
            pointsScreen += 2
            pointsReading += 2
          } else {
            pointsScreen += 3
            pointsReading += 3
          }
        }
      }

      // tablet
      if (this.screens[1].selected) {
        if (this.screens[1].value) {
          if (this.screens[1].value === 0) {
            pointsScreen += 2
            pointsReading += 1
          } else if (this.screens[1].value === 1) {
            pointsScreen += 2
            pointsReading += 2
          } else {
            pointsScreen += 3
            pointsReading += 3
          }
        }
      }

      // desktop
      if (this.screens[2].selected) {
        if (this.screens[2].value) {
          if (this.screens[2].value === 0) {
            pointsScreen += 2
          } else if (this.screens[2].value === 1) {
            pointsScreen += 3
          } else {
            pointsScreen += 4
          }
        }
      }

      // laptop
      if (this.screens[3].selected) {
        if (this.screens[3].value) {
          if (this.screens[3].value === 0) {
            pointsScreen += 2
          } else if (this.screens[3].value === 1) {
            pointsScreen += 3
          } else {
            pointsScreen += 4
          }
        }
      }

      // magazine
      if (this.screens[4].selected) {
        if (this.screens[4].value) {
          if (this.screens[4].value === 0) {
            pointsScreen += 2
            pointsReading += 1
          } else if (this.screens[4].value === 1) {
            pointsScreen += 2
            pointsReading += 2
          } else {
            pointsScreen += 3
            pointsReading += 3
          }
        }
      }

      // book
      if (this.screens[5].selected) {
        if (this.screens[5].value) {
          if (this.screens[5].value === 0) {
            pointsScreen += 2
            pointsReading += 1
          } else if (this.screens[5].value === 1) {
            pointsScreen += 3
            pointsReading += 2
          } else {
            pointsScreen += 4
            pointsReading += 3
          }
        }
      }
    }

    // transport
    if (this.transport) {
      // transport
      if (this.transport[0].selected) {
        if (this.transport[0].value) {
          if (this.transport[0].value === 0) {
            pointsSun += 1
          } else if (this.transport[0].value === 1) {
            pointsSun += 3
          } else {
            pointsSun += 3
          }
        }
      }

      // business
      if (this.transport[1].selected) {
        if (this.transport[1].value) {
          if (this.transport[1].value === 0) {
            pointsSun += 1
          } else if (this.transport[1].value === 1) {
            pointsSun += 3
          } else {
            pointsSun += 3
          }
        }
      }

      // private
      if (this.transport[2].selected) {
        if (this.transport[2].value) {
          if (this.transport[2].value === 0) {
            pointsSun += 1
          } else if (this.transport[2].value === 1) {
            pointsSun += 2
          } else {
            pointsSun += 3
          }
        }
      }

      // motorcycle
      if (this.transport[3].selected) {
        if (this.transport[3].value) {
          if (this.transport[3].value === 0) {
            pointsSun += 1
          } else if (this.transport[3].value === 1) {
            pointsSun += 3
          } else {
            pointsSun += 3
          }
        }
      }

      // bicycle
      if (this.transport[4].selected) {
        if (this.transport[4].value) {
          if (this.transport[4].value === 0) {
            pointsSun += 1
          } else if (this.transport[4].value === 1) {
            pointsSun += 2
          } else {
            pointsSun += 2
          }
        }
      }

      // public transport
      if (this.transport[5].selected) {
        if (this.transport[5].value) {
          if (this.transport[5].value === 0) {
            pointsSun += 1
          } else if (this.transport[5].value === 1) {
            pointsSun += 2
          } else {
            pointsSun += 3
          }
        }
      }
    }

    // sports
    if (this.sports) {
      // watersports
      if (this.sports[0].selected) {
        pointsSun += 1
        pointsSport += 1
      }

      // running
      if (this.sports[1].selected) {
        pointsSun += 1
        pointsSport += 1
      }

      // cycling
      if (this.sports[2].selected) {
        pointsSun += 1
        pointsSport += 1
      }

      // wintersports
      if (this.sports[3].selected) {
        pointsSun += 1
        pointsSport += 2
      }

      // fitness
      if (this.sports[4].selected) {
        // empty
      }

      // hiking
      if (this.sports[5].selected) {
        pointsSun += 1
        pointsSport += 1
      }

      // golf
      if (this.sports[6].selected) {
        pointsSun += 1
        pointsSport += 2
      }

      // tennis
      if (this.sports[7].selected) {
        pointsSun += 1
        pointsSport += 1
      }

      // ballsports
      if (this.sports[8].selected) {
        // empty
      }
    }

    // activities
    if (this.activities) {
      // gaming / fishing
      if (this.activities[0].selected) {
        pointsSun += 1
        pointsSport += 1
      }

      // goint out / gardening
      if (this.activities[1].selected) {
        pointsSun += 1
      }

      // cooking
      if (this.activities[2].selected) {
        // empty
      }

      // shopping
      if (this.activities[3].selected) {
        pointsSun += 1
      }

      // festival / hiking
      if (this.activities[4].selected) {
        pointsSun += 1
      }

      // music / mind games
      if (this.activities[5].selected) {
        pointsSport += 1
      }

      // terrace
      if (this.activities[6].selected) {
        pointsSun += 1
      }

      // cycling
      if (this.activities[7].selected) {
        pointsSun += 1
        pointsSport += 1
      }

      // movies / tv
      if (this.activities[8].selected) {
        // empty
      }

      // culture / city-trip
      if (this.activities[9].selected) {
        pointsSun += 1
      }

      // adventure / hobby
      if (this.activities[10].selected) {
        // empty
      }

      // study / reading
      if (this.activities[11].selected) {
        // empty
      }
    }

    let showReadingGlass = false
    let showScreenGlass = false
    let showSunGlass = false
    let showSportGlass = false
    let showLenses = true

    // multifocal or single glasses
    if ((this.age >= 16) && (this.age < 43)) {
      this.adviceMultifocal = false // single
    } else if (this.age >= 43) {
      this.adviceMultifocal = true // multi
    }

    // reading glasses
    if (pointsReading >= 5) {
      if (this.glasses[2].selected) {
        showReadingGlass = !(this.glasses[1].value === 2)
      } else {
        showReadingGlass = true
      }
    }

    // screen glasses
    if (this.glasses[1].selected) {
      showScreenGlass = !(this.glasses[1].value === 2)
    } else {
      if (pointsScreen >= 6) {
        showScreenGlass = true
      }
    }

    // sun glasses
    if (this.glasses[3].selected) {
      showSunGlass = !(this.glasses[3].value === 2)
    } else {
      if (pointsSun >= 8) {
        showSunGlass = true
      }
    }

    // sport glasses
    if (this.glasses[4].selected) {
      showSportGlass = !(this.glasses[4].value === 2)
    } else {
      if (pointsSport >= 3) {
        showSportGlass = true
      }
    }

    // @todo show lenses logic
    // @todo show reading glasses logic
    // @todo show sports glasses logic
    showScreenGlass = true
    showLenses = false

    // advice
    this.adviceLenses = showLenses
    this.adviceReadingGlasses = showReadingGlass
    this.adviceScreenGlasses = showScreenGlass
    this.adviceSportGlasses = showSportGlass
    this.adviceSunGlasses = showSunGlass
  }

  private updateImageTiles (type: SelectionType, imagePath: string, titles: string[], disabledItems: number[] = []) {
    const itemList = this.getItemList(type)
    itemList.forEach((item: Tile, index: number) => {
      const imageNumber = `${index + 1}`
      item.src = `${imagePath}_${imageNumber.padStart(2, '0')}.png`
      if (titles[index]) {
        item.title = titles[index]
      }
      if (disabledItems.indexOf(index) !== -1) {
        item.enabled = false
        item.selected = false
      } else {
        item.enabled = true
      }
    })
  }

  private createImageTiles (
    type: SelectionType,
    amount: number,
    hasValue: boolean = false,
    selected: number[] = [],
    values: number[] = []
  ) {
    const itemList = this.getItemList(type)

    for (let i = 0; i < amount; i++) {
      const dataObject: Tile = { id: `${i}`, title: '', src: '', selected: false, enabled: true }
      if (selected.indexOf(i) !== -1) {
        dataObject.selected = true
      }
      if (hasValue) {
        if (values.length > i + 1) {
          dataObject.value = values[i]
        } else {
          dataObject.value = 1
        }
      }
      itemList.push(dataObject)
    }
  }
}
